import { createContext, type ReactNode, useContext } from 'react';
import type { FilterLineItem, FilterOperatorType, FilterValueConfig } from '../../utils/filterSettings';

/**
 * Column filter representation
 * Used to store operator type and value for a single column filter
 */
export type ColumnFilter = {
  operatorType?: FilterOperatorType;
  value?: string;
};

/**
 * Context for column filter configuration and state
 * Provides filter configuration and current filter state to ColumnMenu
 */
export type ColumnFilterContextValue = {
  filterId?: string;
  filterLabel?: string;
  filterValueConfig?: FilterValueConfig;
  filterOperatorTypeConfig?: FilterOperatorType[];
  currentFilters?: FilterLineItem[];
  onAddFilter?: (filter: ColumnFilter) => void;
  onRemoveFilter?: (filterId: string) => void;
};

const ColumnFilterContext = createContext<ColumnFilterContextValue | undefined>(undefined);

type ColumnFilterProviderProps = {
  children: ReactNode;
  filterId?: string;
  filterLabel?: string;
  filterValueConfig?: FilterValueConfig;
  filterOperatorTypeConfig?: FilterOperatorType[];
  currentFilters?: FilterLineItem[];
  onAddFilter?: (filter: ColumnFilter) => void;
  onRemoveFilter?: (filterId: string) => void;
};

function ColumnFilterProvider({
  children,
  filterId,
  filterLabel,
  filterValueConfig,
  filterOperatorTypeConfig,
  currentFilters,
  onAddFilter,
  onRemoveFilter,
}: ColumnFilterProviderProps) {
  return (
    <ColumnFilterContext.Provider
      value={{
        filterId,
        filterLabel,
        filterValueConfig,
        filterOperatorTypeConfig,
        currentFilters,
        onAddFilter,
        onRemoveFilter,
      }}
    >
      {children}
    </ColumnFilterContext.Provider>
  );
}

function useColumnFilter() {
  const context = useContext(ColumnFilterContext);
  return context; // Can be undefined if not within a provider
}

export { ColumnFilterProvider, useColumnFilter };
